import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Card,
  CardContent,
  Grid,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CardGiftcard as GiftCardIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon
} from '@mui/icons-material';
import { giftCardAPI } from '../../Api';

const GiftCardsList = () => {
  // Состояния для данных
  const [giftCards, setGiftCards] = useState([]);
  const [stats, setStats] = useState(null);
  const [priceList, setPriceList] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  // Состояния для модального окна
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCard, setEditingCard] = useState(null);
  const [formData, setFormData] = useState({ url: '', nominal: '' });
  const [formErrors, setFormErrors] = useState({});

  // Состояния для удаления
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [cardToDelete, setCardToDelete] = useState(null);

  // Загрузка данных при монтировании и изменении параметров
  useEffect(() => {
    fetchGiftCards();
    fetchStats();
    fetchPriceList();
  }, [page, rowsPerPage, search]);

  // Получение списка подарочных карт
  const fetchGiftCards = async () => {
    setLoading(true);
    try {
      const pageNum = page + 1;
      const response = await giftCardAPI.getGiftCards(pageNum, rowsPerPage, search);
      
      if (response.data && response.data.success) {
        setGiftCards(response.data.data);
        setTotal(response.data.pagination.total);
      }
    } catch (error) {
      console.error('Error fetching gift cards:', error);
    }
    setLoading(false);
  };

  // Получение статистики
  const fetchStats = async () => {
    try {
      const response = await giftCardAPI.getGiftCardStats();
      if (response.data && response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  // Получение прайс-листа
  const fetchPriceList = async () => {
    try {
      const response = await giftCardAPI.getGiftCardPriceList();
      if (response.data && response.data.success) {
        setPriceList(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching price list:', error);
    }
  };

  // Обработчики пагинации
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Обработчик поиска
  const handleSearchChange = (event) => {
    setSearch(event.target.value);
    setPage(0);
  };

  // Открытие модального окна для создания/редактирования
  const handleOpenDialog = (card = null) => {
    setEditingCard(card);
    setFormData(card ? { url: card.url, nominal: card.nominal } : { url: '', nominal: '' });
    setFormErrors({});
    setDialogOpen(true);
  };

  // Закрытие модального окна
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingCard(null);
    setFormData({ url: '', nominal: '' });
    setFormErrors({});
  };

  // Валидация формы
  const validateForm = () => {
    const errors = {};
    
    if (!formData.url.trim()) {
      errors.url = 'URL обязателен';
    }
    
    if (!formData.nominal || formData.nominal <= 0) {
      errors.nominal = 'Номинал должен быть больше 0';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Сохранение подарочной карты
  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      if (editingCard) {
        await giftCardAPI.updateGiftCard(editingCard._id, formData);
      } else {
        await giftCardAPI.createGiftCard(formData);
      }
      
      handleCloseDialog();
      fetchGiftCards();
      fetchStats();
      fetchPriceList();
    } catch (error) {
      console.error('Error saving gift card:', error);
    }
  };

  // Открытие диалога удаления
  const handleOpenDeleteDialog = (card) => {
    setCardToDelete(card);
    setDeleteDialogOpen(true);
  };

  // Удаление подарочной карты
  const handleDelete = async () => {
    if (!cardToDelete) return;

    try {
      await giftCardAPI.deleteGiftCard(cardToDelete._id);
      setDeleteDialogOpen(false);
      setCardToDelete(null);
      fetchGiftCards();
      fetchStats();
      fetchPriceList();
    } catch (error) {
      console.error('Error deleting gift card:', error);
    }
  };

  // Форматирование валюты
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB'
    }).format(amount);
  };

  // Форматирование USDT
  const formatUSDT = (amount) => {
    return `${amount} USDT`;
  };

  return (
    <Box sx={{ mt: 8, p: 4, boxShadow: 3, borderRadius: 2, bgcolor: "#fefcfb" }}>
      <Typography variant="h5" align="center" gutterBottom>
        Управление подарочными картами
      </Typography>

      {/* Прайс-лист */}
      {priceList && (
        <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
              <MoneyIcon sx={{ mr: 1 }} />
              Прайс-лист подарочных карт
              <Chip 
                label={`Курс USDT: ${formatCurrency(priceList.tetherRate)}`}
                sx={{ ml: 2, bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                size="small"
              />
              <Chip 
                label={`Курс для клиента: ${formatCurrency(priceList.clientRate)}`}
                sx={{ ml: 1, bgcolor: 'rgba(255,255,255,0.3)', color: 'white' }}
                size="small"
              />
            </Typography>
            <Grid container spacing={2}>
              {priceList.priceList.map((item, idx) => (
                <Grid item xs={6} sm={4} md={3} lg={2} key={idx}>
                  <Card sx={{ 
                    bgcolor: 'rgba(255,255,255,0.1)', 
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    transition: 'all 0.2s ease',
                    '&:hover': { transform: 'translateY(-2px)', bgcolor: 'rgba(255,255,255,0.15)' }
                  }}>
                    <CardContent sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold', mb: 1 }}>
                        {formatUSDT(item.nominal)}
                      </Typography>
                      
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', mb: 0.5 }}>
                        Полная цена:
                      </Typography>
                      <Typography variant="body1" sx={{ color: 'white', fontWeight: 'bold', mb: 1 }}>
                        {formatCurrency(item.rubPrice)}
                      </Typography>
                      
                      <Typography variant="h6" sx={{ color: '#4CAF50', fontWeight: 'bold', mb: 1 }}>
                        {formatCurrency(item.finalPrice)}
                      </Typography>
                      
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', mb: 0.5 }}>
                        Клиент платит:
                      </Typography>
                      <Typography variant="h6" sx={{ color: '#2196F3', fontWeight: 'bold', mb: 1 }}>
                        {formatCurrency(item.clientPrice)}
                      </Typography>
                      
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', mb: 0.5 }}>
                        Прибыль:
                      </Typography>
                      <Chip 
                        label={formatCurrency(item.profit)}
                        size="small"
                        sx={{ bgcolor: '#FF9800', color: 'white', fontSize: '0.75rem', fontWeight: 'bold' }}
                      />
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
            <Typography variant="caption" sx={{ display: 'block', mt: 2, opacity: 0.8 }}>
              Обновлено: {new Date(priceList.lastUpdated).toLocaleString('ru-RU')}
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Статистика */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <GiftCardIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Box>
                    <Typography variant="h6">{stats.totalCards}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Всего карт
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <MoneyIcon sx={{ mr: 1, color: 'success.main' }} />
                  <Box>
                    <Typography variant="h6">{formatUSDT(stats.totalValue)}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Общая стоимость
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TrendingUpIcon sx={{ mr: 1, color: 'info.main' }} />
                  <Box>
                    <Typography variant="h6">{formatUSDT(stats.avgValue.toFixed(2))}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Средний номинал
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <MoneyIcon sx={{ mr: 1, color: 'warning.main' }} />
                  <Box>
                    <Typography variant="h6">{formatUSDT(stats.maxValue)}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Макс. номинал
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Панель управления */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <TextField
          label="Поиск по URL или номиналу"
          variant="outlined"
          size="small"
          value={search}
          onChange={handleSearchChange}
          sx={{ minWidth: 300 }}
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Добавить карту
        </Button>
      </Box>

      {/* Таблица */}
      <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f3f6fa' }}>
                URL
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f3f6fa' }}>
                Номинал
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f3f6fa' }}>
                Создана
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f3f6fa' }}>
                Обновлена
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f3f6fa' }}>
                Действия
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : giftCards.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography color="text.secondary">
                    Подарочные карты не найдены
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              giftCards.map((card, idx) => (
                <TableRow
                  key={card._id}
                  sx={{
                    backgroundColor: idx % 2 === 0 ? '#fff' : '#f9fbfd',
                    '&:hover': { backgroundColor: '#e3f0ff' }
                  }}
                >
                  <TableCell>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        maxWidth: 300, 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {card.url}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={formatUSDT(card.nominal)}
                      color="primary"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    {new Date(card.created_at).toLocaleString('ru-RU')}
                  </TableCell>
                  <TableCell>
                    {new Date(card.updated_at).toLocaleString('ru-RU')}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(card)}
                      color="primary"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDeleteDialog(card)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      {/* Диалог создания/редактирования */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingCard ? 'Редактировать подарочную карту' : 'Добавить подарочную карту'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="URL подарочной карты"
            fullWidth
            variant="outlined"
            value={formData.url}
            onChange={(e) => setFormData({ ...formData, url: e.target.value })}
            error={!!formErrors.url}
            helperText={formErrors.url}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Номинал (USDT)"
            type="number"
            fullWidth
            variant="outlined"
            value={formData.nominal}
            onChange={(e) => setFormData({ ...formData, nominal: Number(e.target.value) })}
            error={!!formErrors.nominal}
            helperText={formErrors.nominal}
            inputProps={{ min: 0, step: 0.01 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Отмена</Button>
          <Button onClick={handleSave} variant="contained">
            {editingCard ? 'Сохранить' : 'Создать'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог удаления */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Подтверждение удаления</DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            Вы уверены, что хотите удалить подарочную карту с номиналом{' '}
            <strong>{cardToDelete && formatUSDT(cardToDelete.nominal)}</strong>?
            Это действие нельзя отменить.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Отмена</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Удалить
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GiftCardsList;
